import type { TestGroupingItem } from '@vedalib/editor/lib/elements'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import { isEmptyRtValue } from '@vedalib/rich-text'
import cn from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { useState, useEffect, useRef } from 'react'
import { useContext } from 'use-context-selector'

import { getObjectFitDivMap } from 'components/Editor/EditorElements/utils/getObjectFitDivMap'
import { RichTextViewer } from 'components/form/RichText/RichTextViewer'
import { t } from 'services/Translation'
import { useUrlByUsage } from 'utils/files'

import TestGroupingContext from '../../TestGroupingContext'
import IconLeft from '../../assets/icons-for-elements-left.svg?react'
import IconRight from '../../assets/icons-for-elements-right.svg?react'
import { getNavPositionStyle } from '../../helper'
import * as s from './MobileAnswerStack.module.scss'

interface IMobileAnswerStackProps {
  answers: TestGroupingItem[]
}

const MobileAnswerStack = ({ answers }: IMobileAnswerStackProps) => {
  const { styles, shuffledAnswers } = useContext(TestGroupingContext)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [navKey, setNavKey] = useState(0)
  const totalItems = answers.length
  const prevLengthRef = useRef(totalItems)

  useEffect(() => {
    if (totalItems < prevLengthRef.current && currentIndex >= totalItems && totalItems > 0) {
      setCurrentIndex(totalItems - 1)
    }
    prevLengthRef.current = totalItems
  }, [totalItems, currentIndex])

  const cardSize = {
    width: styles.common.width,
    height: styles.common.height,
  }

  const counterCurrent = currentIndex + 1
  const counterTotal = shuffledAnswers.length

  const handlePrevious = (e: React.MouseEvent) => {
    e.stopPropagation()
    setNavKey((prev) => prev + 1)
    setCurrentIndex((prev) => (prev + 1) % totalItems)
  }

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation()
    setNavKey((prev) => prev + 1)
    setCurrentIndex((prev) => (prev === 0 ? totalItems - 1 : prev - 1))
  }

  if (totalItems === 0) {
    return null
  }

  return (
    <div className={s.stackWrapper} style={cardSize}>
      <div className={s.stackContainer} style={cardSize}>
        {answers.map((item, index) => {
          const isCurrent = index === currentIndex

          return (
            <MobileAnswerCard
              cardSize={cardSize}
              counterCurrent={counterCurrent}
              counterTotal={counterTotal}
              isCurrent={isCurrent}
              item={item}
              key={item.value}
              navKey={navKey}
            />
          )
        })}
      </div>

      {totalItems > 1 && (
        <>
          <div
            className={cn(s.navButtonOuter, s.prev)}
            onClick={handlePrevious}
            style={{ color: styles.common.color }}
          >
            <IconLeft />
          </div>
          <div
            className={cn(s.navButtonOuter, s.next)}
            onClick={handleNext}
            style={{ color: styles.common.color }}
          >
            <IconRight />
          </div>
        </>
      )}
    </div>
  )
}

interface IMobileAnswerCardProps {
  item: TestGroupingItem
  isCurrent: boolean
  cardSize: { width?: number | string; height?: number | string }
  navKey: number
  counterCurrent: number
  counterTotal: number
}

const MobileAnswerCard = ({
  item,
  isCurrent,
  cardSize,
  navKey,
  counterCurrent,
  counterTotal,
}: IMobileAnswerCardProps) => {
  const { styles, font, value, isFill } = useContext(TestGroupingContext)
  const items = value.items
  const groupIndex = items.findIndex((group) => group.items?.some((a) => a.value === item.value))
  const imageUrl = useUrlByUsage(item.image, FileUsageTypeEnum.image)
  const hasAnswerImage = !!item.image?.id
  const backgroundImage = (hasAnswerImage && `url(${imageUrl})`) || undefined
  const hasLabel = item.label && !isEmptyRtValue(item.label)
  const labelClasses = cn(s.label, { [s.placeholder]: !hasLabel })
  const showPlaceholder = !hasLabel && !(item?.image?.id && hasAnswerImage)

  return (
    <motion.div
      animate={{ opacity: isCurrent ? 1 : 0 }}
      className={s.cardWrapper}
      initial={{ opacity: 0.5 }}
      key={isCurrent ? `current-${navKey}` : item.value}
      style={{
        zIndex: isCurrent ? 2 : 1,
      }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
    >
      <div
        className={s.answerCard}
        style={{
          ...cardSize,
          ...styles.common,
          ...styles.answer,
          backgroundImage,
          backgroundSize: getObjectFitDivMap(item.image?.objectFit),
          outlineColor: styles.dragButton.backgroundColor || 'var(--brand-primaryColor)',
        }}
      >
        <div
          className={s.mask}
          style={{
            backgroundColor: item.overlay || undefined,
            borderRadius: styles.common.borderRadius,
          }}
        />
        <div
          className={labelClasses}
          style={{
            padding: styles.indents.padding,
            justifyContent: styles.answer.alignItems,
          }}
        >
          <div className={s.textInner} style={font?.base}>
            {showPlaceholder ? (
              t('elements.testGrouping.answer.placeholder')
            ) : (
              <RichTextViewer styles={font} value={item.label} />
            )}
          </div>
        </div>
        <div
          className={s.navigation}
          style={getNavPositionStyle(styles.answerNavigation?.iconsAlign, styles.indents?.padding)}
        >
          <AnimatePresence mode='popLayout'>
            <motion.div
              animate={{ opacity: 1 }}
              className={s.counter}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              key={`${counterCurrent}-${counterTotal}`}
              style={font?.answerNumbering}
              transition={{ duration: 0.2 }}
            >
              {counterCurrent}/{counterTotal}
            </motion.div>
          </AnimatePresence>
        </div>
        {groupIndex !== -1 && isFill && (
          <div className={s.groupBadge}>
            {t('elements.grouping.tags.group')} {groupIndex + 1}
          </div>
        )}
      </div>
    </motion.div>
  )
}

export default MobileAnswerStack
